import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	inject,
	input,
	output,
	signal,
	viewChild,
} from '@angular/core';
import {
	DestroyService,
	DialogService,
	ImageCropperDialogComponent,
} from '@context/frontend/common';
import {
	AuthUser,
	getDisplayName,
	getInitials,
	User,
} from '@context/shared/types/user';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiButton, TuiIconPipe, TuiSizeXS, TuiSizeXXL } from '@taiga-ui/core';
import { TuiAvatar, TuiAvatarOutline } from '@taiga-ui/kit';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';

const FileAcceptImageTypes = ['*/*', 'image/png', 'image/jpg', 'image/jpeg'];
export type FileAcceptImageType = (typeof FileAcceptImageTypes)[number];

@Component({
	imports: [
		TuiAvatar,
		TuiAvatarOutline,
		TuiButton,
		TuiIconPipe,
		TranslocoModule,
	],
	selector: 'ctx-avatar',
	templateUrl: 'avatar.component.html',
	styleUrl: 'avatar.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [DestroyService],
	host: {
		'[attr.disabled]': 'disabled()',
		'[attr.can-click]': 'canUpload()',
		'[attr.tabindex.0]': 'canUpload()',
	},
})
export class AvatarComponent {
	readonly fileUploadInputRef =
		viewChild<ElementRef<HTMLInputElement>>('fileUploadInput');

	readonly icon = input('@tui.user');

	readonly user = input<User | AuthUser | null>(null);

	readonly size = input<TuiSizeXS | TuiSizeXXL>('m');

	readonly disabled = input(false);

	readonly canUpload = input(false);

	readonly fileChange = output<File>();
	readonly fileRemove = output<void>();

	private readonly dialog = inject(DialogService);

	readonly accept = FileAcceptImageTypes.join(', ');

	/**
	 * used for the cropper, should only be used until cropper has resolved and then remove
	 */
	readonly imageFile = signal<File | null>(null);

	readonly imageCropperDialog = () =>
		this.dialog.open<File | null>(
			new PolymorpheusComponent(ImageCropperDialogComponent),
			ImageCropperDialogComponent.Options({
				data: { imageFile: this.imageFile() },
			}),
		);

	get src() {
		const user = this.user();
		if (!user) return null;
		// Only org Users have thumbnailUrl; AuthUsers show initials only
		if ('thumbnailUrl' in user && user.thumbnailUrl)
			return user.thumbnailUrl;
		return this.initials;
	}

	get initials() {
		const user = this.user();
		if (!user) return '';
		const name = getDisplayName(user);
		return getInitials(name) ?? '';
	}

	/** True when the value is an org User with a thumbnail (not AuthUser). */
	hasThumbnail(value: User | AuthUser): value is User {
		return 'thumbnailUrl' in value && !!value.thumbnailUrl;
	}

	onUploadImage() {
		if (!this.canUpload()) return;
		this.fileUploadInputRef()?.nativeElement?.click();
	}

	readonly onRemoveImage = () => this.fileRemove.emit();

	onFileChange() {
		const fileInput = this.fileUploadInputRef()?.nativeElement;
		if (!fileInput) return Promise.resolve();

		this.imageFile.set(fileInput.files?.item(0) ?? null);
		if (this.imageFile()) {
			this.imageCropperDialog().subscribe({
				next: (data) => this.onImageCropperDismiss(data),
			});
		}
		return Promise.resolve();
	}

	onImageCropperDismiss(file: File | null) {
		this.imageFile.set(null);
		const fileInput = this.fileUploadInputRef()?.nativeElement;
		if (fileInput) {
			fileInput.value = '';
		}

		if (!file) return;
		this.fileChange.emit(file);
	}
}
